/* 식 입구. 모델이 쓴 LaTeX 는 Compute Engine 으로 읽어 구문 트리(MathJSON)로 만들고, 그 트리에서 mathcore 문법의 식을 다시 쓴다.
 * 문자열을 고쳐 쓰지 않는다. 트리가 mathcore 문법에 없는 노드(허수 단위, 오류, 모르는 함수)를 가지면 고치지 않고 사유와 함께 거부한다.
 * 화면에 서는지는 mathcore 가 개체를 만들 때 정한다(실행기의 사후조건). 여기서는 읽기와 다시 쓰기만 한다 */
import * as mathjs from 'mathjs';
import { NOT_SYMBOLS, canonicalName, newEngine } from './engine';

const ce = newEngine();

type Json = number | string | { num?: string; sym?: string; str?: string } | Json[];

/** 수식 모드 표시($, \( \), \[ \])는 식의 내용이 아니라 감싸개다 */
export function unwrapMath(raw: string): string {
  let text = raw.trim();
  for (let i = 0; i < 4; i += 1) {
    const before = text;
    for (const [open, close] of [['$$', '$$'], ['$', '$'], ['\\(', '\\)'], ['\\[', '\\]']]) {
      if (text.length > open.length + close.length && text.startsWith(open) && text.endsWith(close)) text = text.slice(open.length, text.length - close.length).trim();
    }
    if (text === before) break;
  }
  return text;
}

const isLetter = (ch: string) => ch.length === 1 && ch.toLowerCase() !== ch.toUpperCase();

const FUNCS: Record<string, string> = {
  Sin: '\\sin', Cos: '\\cos', Tan: '\\tan', Cot: '\\cot', Sec: '\\sec', Csc: '\\csc',
  Arcsin: '\\arcsin', Arccos: '\\arccos', Arctan: '\\arctan', Sinh: '\\sinh', Cosh: '\\cosh', Tanh: '\\tanh', Ln: '\\ln',
};
// \leqq, \geqq 는 CE 가 LessOverEqual, GreaterOverEqual 로 읽는다(실측 2026-10-15). 뜻은 같다
// mathcore 는 \le, \ge 만 부등호로 읽는다. <=, >= 는 개체가 무효이고 \leq 는 값 없는 슬라이더 q 를 세운다(docs/palette/algebraric-point.md 1절, 화면 실측)
const RELATIONS: Record<string, string> = { Equal: '=', Less: '<', LessEqual: '\\le ', LessOverEqual: '\\le ', Greater: '>', GreaterEqual: '\\ge ', GreaterOverEqual: '\\ge ' };

class Unsupported extends Error {}

interface Printed { s: string; prec: number }
const ATOM = 5; const POW = 4; const MUL = 3; const NEG = 2; const ADD = 1;

const wrap = (p: Printed, min: number): string => (p.prec < min ? `(${p.s})` : p.s);
const braced = (p: Printed): string => `{${p.s}}`;

function numberText(value: number): Printed {
  if (!Number.isFinite(value)) throw new Unsupported('수가 아닌 값(NaN, Infinity)');
  return { s: String(value), prec: value < 0 ? NEG : ATOM };
}

function head(node: Json): string | null {
  return Array.isArray(node) && typeof node[0] === 'string' ? node[0] : null;
}

/** mathcore 파서는 \int_{하한}^{상한}식\differentialD 변수 꼴만 읽는다(dx 는 개체가 무효, 부정적분과 변수 한도는 못 읽는다) */
function printIntegral(a: Json[]): Printed {
  const [fn, limits] = a;
  if (head(fn) !== 'Function' || head(limits) !== 'Limits') throw new Unsupported('부정적분은 mathcore 식에 없다. 한도를 정한 정적분만 쓴다');
  const [, body] = fn as Json[];
  const [, variable, low, high] = limits as Json[];
  if (variable !== 'x' && variable !== 'y') throw new Unsupported('정적분의 변수는 x 또는 y 여야 한다');
  const inner = head(body) === 'Block' ? (body as Json[])[1] : body;
  if (numberOf(low) === null || numberOf(high) === null) throw new Unsupported('정적분의 한도는 수여야 한다');
  return { s: `\\int_{${print(low).s}}^{${print(high).s}}${wrap(print(inner), MUL)}\\differentialD ${variable}`, prec: ATOM };
}

function print(node: Json): Printed {
  if (typeof node === 'number') return numberText(node);
  if (typeof node === 'string') {
    if (node === 'Pi') return { s: '\\pi', prec: ATOM };
    if (node === 'ExponentialE') return { s: 'e', prec: ATOM };
    if (isLetter(node)) return { s: node, prec: ATOM };
    throw new Unsupported(`기호 ${node} 는 mathcore 식에 없다`);
  }
  if (!Array.isArray(node)) {
    if (node.num !== undefined) return numberText(Number(node.num));
    if (node.sym !== undefined) return print(node.sym);
    throw new Unsupported('문자열 값은 식에 쓰지 않는다');
  }
  const [h, ...a] = node as [string, ...Json[]];
  switch (h) {
    case 'Add': {
      let out = '';
      a.forEach((term, i) => {
        const t = print(term);
        const text = t.prec < ADD ? `(${t.s})` : t.s;
        out += i === 0 ? text : text.startsWith('-') ? text : `+${text}`;
      });
      return { s: out, prec: ADD };
    }
    case 'Subtract': return { s: `${print(a[0]).s}-${wrap(print(a[1]), MUL)}`, prec: ADD };
    case 'Negate': return { s: `-${wrap(print(a[0]), MUL)}`, prec: NEG };
    case 'Multiply': {
      const parts = a.map((f) => print(f));
      let out = parts[0].prec < MUL && !parts[0].s.startsWith('-') ? `(${parts[0].s})` : parts[0].s;
      if (parts[0].s.startsWith('-') && parts[0].prec < NEG) out = `(${parts[0].s})`;
      for (const p of parts.slice(1)) out += `\\cdot ${wrap(p, MUL)}`;
      return { s: out, prec: MUL };
    }
    case 'Divide': return { s: `\\frac${braced(print(a[0]))}${braced(print(a[1]))}`, prec: ATOM };
    case 'Rational': {
      const n = Number(a[0]); const d = Number(a[1]);
      return { s: `${n < 0 ? '-' : ''}\\frac{${Math.abs(n)}}{${d}}`, prec: n < 0 ? NEG : ATOM };
    }
    case 'Power': return { s: `${wrap(print(a[0]), ATOM)}^${braced(print(a[1]))}`, prec: POW };
    case 'Sqrt': return { s: `\\sqrt${braced(print(a[0]))}`, prec: ATOM };
    case 'Root': return { s: `${wrap(print(a[0]), ATOM)}^{\\frac{1}{${print(a[1]).s}}}`, prec: POW };
    case 'Integrate': return printIntegral(a);
    case 'Abs': return { s: `\\left|${print(a[0]).s}\\right|`, prec: ATOM };
    case 'Exp': return { s: `e^${braced(print(a[0]))}`, prec: POW };
    case 'Log': return a.length > 1 ? { s: `\\log_${braced(print(a[1]))}(${print(a[0]).s})`, prec: ATOM } : { s: `\\log(${print(a[0]).s})`, prec: ATOM };
    case 'Lb': return { s: `\\log_{2}(${print(a[0]).s})`, prec: ATOM };
    case 'Lg': return { s: `\\log_{10}(${print(a[0]).s})`, prec: ATOM };
    default: {
      if (h in FUNCS) return { s: `${FUNCS[h]}(${print(a[0]).s})`, prec: ATOM };
      if (h in RELATIONS) return { s: `${print(a[0]).s}${RELATIONS[h]}${print(a[1]).s}`, prec: 0 };
      if (h === 'Complex') throw new Unsupported('허수 단위가 읽혔다(함수 이름 앞에 역슬래시가 없다는 뜻)');
      if (h === 'Apply' || h === 'InverseFunction' || (h.length === 1 && isLetter(h))) throw new Unsupported(`${h === 'Apply' || h === 'InverseFunction' ? String(Array.isArray(a[0]) ? a[0][1] : a[0]) : h} 는 다른 곡선의 이름이다. 이 식 하나로는 그릴 수 없고 그 곡선의 식을 대입해야 한다`);
      throw new Unsupported(`${h} 는 mathcore 식에 없다`);
    }
  }
}

/** 트리 안의 오류 노드와 문법 밖 상수를 찾는다 */
function firstProblem(node: Json): string | null {
  if (!Array.isArray(node)) return null;
  if (node[0] === 'Error') return `파서 오류 ${String(node[1])}`;
  if (node[0] === 'Complex' || node[0] === 'Nothing') return '식에 허수 단위나 빈 항이 읽혔다';
  for (const child of node.slice(1)) { const p = firstProblem(child as Json); if (p) return p; }
  return null;
}

/* ───────── 구간별 정의(cases) → 조각 ───────── */

/** mathcore 조건 목록이 받는 것은 x(또는 y) 하나에 대한 이어진 부등식뿐이다(실측 2026-10-15: 절댓값, or, 쉼표는 무효). 조건 트리를 그 꼴의 구간들로 바꾼다 */
interface Interval { lo: number | null; hi: number | null; loIn: boolean; hiIn: boolean }

function numberOf(node: Json): number | null {
  if (typeof node === 'number') return node;
  if (node && typeof node === 'object' && !Array.isArray(node) && node.num !== undefined) return Number(node.num);
  if (head(node) === 'Negate') { const v = numberOf((node as Json[])[1]); return v === null ? null : -v; }
  if (head(node) === 'Rational') { const n = numberOf((node as Json[])[1]); const d = numberOf((node as Json[])[2]); return n === null || d === null ? null : n / d; }
  return null;
}

function intervalsOf(cond: Json, v: string): Interval[] | null {
  const h = head(cond);
  if (!h || !(h in RELATIONS) || h === 'Equal') return null;
  const args = (cond as Json[]).slice(1);
  const inclusive = h === 'LessEqual' || h === 'GreaterEqual' || h === 'LessOverEqual' || h === 'GreaterOverEqual';
  const ascending = h === 'Less' || h === 'LessEqual' || h === 'LessOverEqual';
  // 이어진 부등식 a<x<b
  if (args.length === 3 && args[1] === v) {
    const a = numberOf(args[0]); const b = numberOf(args[2]);
    if (a === null || b === null) return null;
    return ascending ? [{ lo: a, hi: b, loIn: inclusive, hiIn: inclusive }] : [{ lo: b, hi: a, loIn: inclusive, hiIn: inclusive }];
  }
  if (args.length !== 2) return null;
  const [l, r] = args;
  const sideIsVar = (s: Json) => s === v;
  const sideIsAbs = (s: Json) => head(s) === 'Abs' && (s as Json[])[1] === v;
  let c: number | null = null; let varLeft = true; let abs = false;
  if ((sideIsVar(l) || sideIsAbs(l)) && (c = numberOf(r)) !== null) { varLeft = true; abs = sideIsAbs(l); }
  else if ((sideIsVar(r) || sideIsAbs(r)) && (c = numberOf(l)) !== null) { varLeft = false; abs = sideIsAbs(r); }
  else return null;
  // x < c 꼴로 정규화: 변수가 오른쪽이면 방향을 뒤집는다
  const lessThan = varLeft ? ascending : !ascending;
  if (!abs) return lessThan ? [{ lo: null, hi: c, loIn: false, hiIn: inclusive }] : [{ lo: c, hi: null, loIn: inclusive, hiIn: false }];
  if (c < 0) return lessThan ? [] : [{ lo: null, hi: null, loIn: false, hiIn: false }];
  return lessThan ? [{ lo: -c, hi: c, loIn: inclusive, hiIn: inclusive }] : [{ lo: null, hi: -c, loIn: false, hiIn: inclusive }, { lo: c, hi: null, loIn: inclusive, hiIn: false }];
}

function conditionText(i: Interval, v: string): string {
  const lo = i.lo === null ? '' : `${i.lo}${i.loIn ? ' \\le ' : '<'}`;
  const hi = i.hi === null ? '' : `${i.hiIn ? ' \\le ' : '<'}${i.hi}`;
  return `${lo}${v}${hi}`;
}

export interface ExprPiece { latex: string; condition: string }
export interface ExprRead {
  /** mathcore 문법의 식. 못 읽으면 null */
  latex: string | null;
  /** 구간별 정의(cases)면 조각들. 각 조각은 식{조건} 으로 세운다 */
  pieces?: ExprPiece[];
  /** 못 읽은 구조적 사유 */
  problem: string | null;
  /** 읽은 구문 트리(정규형) */
  json: Json | null;
  /** 값이 없어 못 읽은 기호(정규 이름). 값을 주면 읽힌다 */
  unresolved?: string[];
}

/** f(x)=… 꼴의 왼쪽은 y 다. 함수 이름은 곡선 이름이지 식의 일부가 아니다 */
function normalizeHead(json: Json): Json {
  if (head(json) !== 'Equal') return json;
  const [, lhs, rhs] = json as Json[];
  const fn = head(lhs);
  if (fn && fn.length === 1 && isLetter(fn) && (lhs as Json[]).length === 2 && (lhs as Json[])[1] === 'x') return ['Equal', 'y', rhs];
  return json;
}

function readPieces(json: Json): ExprRead | null {
  if (head(json) !== 'Equal') return null;
  const [, lhs, rhs] = json as Json[];
  if (typeof lhs !== 'string' || head(rhs) !== 'Which') return null;
  const v = lhs === 'y' ? 'x' : lhs === 'x' ? 'y' : null;
  if (!v) return null;
  const args = (rhs as Json[]).slice(1);
  const pieces: ExprPiece[] = [];
  // 「0,& |x|≤1」처럼 칸 안에 쉼표가 들어오면 CE 가 값을 묶음(Tuple, Sequence)으로 읽는다. 묶음이면 빈 항을 뺀 하나만 값이다
  const unwrap = (v: Json): Json => {
    const h = head(v);
    if (h !== 'Tuple' && h !== 'Sequence' && h !== 'Delimiter' && h !== 'Pair' && h !== 'Single') return v;
    const items = (v as Json[]).slice(1).filter((x) => x !== 'Nothing' && (typeof x !== 'string' || x.length !== 1 || !"',;".includes(x)));
    return items.length === 1 ? unwrap(items[0]) : v;
  };
  for (let i = 0; i + 1 < args.length; i += 2) {
    const cond = args[i]; const value = unwrap(args[i + 1]);
    if (cond === 'True') return { latex: null, problem: '구간별 정의의 「그 밖의 경우」는 구간으로 바꿀 수 없다. 조건을 전부 적어야 한다', json };
    const intervals = intervalsOf(cond, v);
    if (!intervals) return { latex: null, problem: `조건 ${JSON.stringify(cond)} 은 ${v} 하나의 부등식 구간으로 바꿀 수 없다`, json };
    let body: string;
    try { body = `${lhs}=${print(value).s}`; } catch (error) { if (error instanceof Unsupported) return { latex: null, problem: error.message, json }; throw error; }
    for (const it of intervals) pieces.push({ latex: body, condition: conditionText(it, v) });
  }
  if (!pieces.length) return { latex: null, problem: '구간별 정의에 조각이 없다', json };
  return { latex: null, pieces, problem: null, json };
}

/* ───────── 역함수 관계 → 원래 함수의 곡선 ───────── */

const INVERSE_MARK = 'INVERSEARG';

/** 트리에 f^{-1}(x) 꼴(Apply(InverseFunction f, x))이 있는가 */
function hasInverse(node: Json): boolean {
  if (!Array.isArray(node)) return false;
  if (node[0] === 'Apply' && head(node[1]) === 'InverseFunction') return true;
  return node.slice(1).some((c) => hasInverse(c as Json));
}

/** 관계 R(x, f^{-1}(x)) 는 f^{-1} 의 그래프 {(x, w)} 다. f 의 그래프는 그 대칭 {(w, x)} 이므로 f^{-1}(x) 자리를 x 로, 나머지 x 를 y 로 바꾼다 */
function swapInverse(node: Json): Json {
  const mark = (n: Json): Json => {
    if (!Array.isArray(n)) return n;
    if (n[0] === 'Apply' && head(n[1]) === 'InverseFunction' && n[2] === 'x') return INVERSE_MARK;
    return n.map((c, i) => (i === 0 ? c : mark(c as Json))) as Json;
  };
  const swap = (n: Json): Json => (n === 'x' ? 'y' : n === INVERSE_MARK ? 'x' : Array.isArray(n) ? n.map((c, i) => (i === 0 ? c : swap(c as Json))) as Json : n);
  return swap(mark(node));
}

/* ───────── 두 번째 문법: 계산기 표기(mathjs). sin(a*x+b), exp(abs(x)-1) 같은 계산기 표기도 받는다 ─────────
 * mathjs 구문 트리를 MathJSON 으로 옮긴다. 문자열(LaTeX)을 거치지 않으므로 mathjs 의 표기 선택(\mathrm 등)이 끼어들지 않는다 */
const MATHJS_FN: Record<string, string> = {
  sin: 'Sin', cos: 'Cos', tan: 'Tan', cot: 'Cot', sec: 'Sec', csc: 'Csc', asin: 'Arcsin', acos: 'Arccos', atan: 'Arctan',
  sinh: 'Sinh', cosh: 'Cosh', tanh: 'Tanh', exp: 'Exp', sqrt: 'Sqrt', abs: 'Abs', ln: 'Ln', cbrt: 'Cbrt',
};
const MATHJS_OP: Record<string, string> = { add: 'Add', subtract: 'Subtract', multiply: 'Multiply', divide: 'Divide', pow: 'Power', unaryMinus: 'Negate', unaryPlus: '', equal: 'Equal', smaller: 'Less', smallerEq: 'LessEqual', larger: 'Greater', largerEq: 'GreaterEqual' };

interface MathjsNode { type: string; op?: string; fn?: string | { name: string }; args?: MathjsNode[]; name?: string; value?: unknown; content?: MathjsNode; object?: { name?: string }; value2?: unknown }

function mathjsToJson(n: MathjsNode): Json {
  switch (n.type) {
    case 'ConstantNode': { const v = Number(n.value); if (!Number.isFinite(v)) throw new Unsupported(`상수 ${String(n.value)} 를 수로 읽지 못한다`); return v; }
    case 'SymbolNode': {
      const name = n.name ?? '';
      if (name === 'pi') return 'Pi';
      if (name === 'e') return 'ExponentialE';
      if (name.length === 1 && isLetter(name)) return name;
      // alpha, theta, a_1 처럼 엔진이 기호로 읽는 이름은 정규 이름으로 받는다. 값은 symbols 로 대입된다
      const canon = canonicalName(name);
      if (canon) return canon;
      throw new Unsupported(`기호 ${name} 는 식에 쓸 수 없다(엔진이 기호로 읽지 못한다)`);
    }
    case 'ParenthesisNode': return mathjsToJson(n.content!);
    case 'OperatorNode': {
      const fn = typeof n.fn === 'string' ? n.fn : '';
      const h = MATHJS_OP[fn];
      if (h === undefined) throw new Unsupported(`연산 ${n.op ?? fn} 는 식에 쓸 수 없다`);
      const args = (n.args ?? []).map(mathjsToJson);
      return h === '' ? args[0] : [h, ...args];
    }
    case 'FunctionNode': {
      const name = typeof n.fn === 'string' ? n.fn : n.fn?.name ?? '';
      const args = (n.args ?? []).map(mathjsToJson);
      if (name === 'log') return args.length > 1 ? ['Log', args[0], args[1]] : ['Ln', args[0]];
      if (name === 'log2') return ['Log', args[0], 2];
      if (name === 'log10') return ['Log', args[0], 10];
      if (name === 'nthRoot') return ['Root', args[0], args[1]];
      if (name === 'cbrt') return ['Root', args[0], 3];
      const h = MATHJS_FN[name];
      if (!h) throw new Unsupported(`함수 ${name} 는 식에 쓸 수 없다`);
      return [h, ...args];
    }
    case 'AssignmentNode': return ['Equal', n.object?.name ?? '', mathjsToJson(n.value as MathjsNode)];
    case 'RelationalNode': {
      const node = n as unknown as { conditionals: string[]; params: MathjsNode[] };
      const h = MATHJS_OP[node.conditionals[0]];
      if (!h || node.params.length !== 2) throw new Unsupported('이어진 부등식은 계산기 표기로 받지 않는다');
      return [h, mathjsToJson(node.params[0]), mathjsToJson(node.params[1])];
    }
    default: throw new Unsupported(`${n.type} 는 식에 쓸 수 없다`);
  }
}

function parseAscii(text: string): { json: Json } | { problem: string } {
  try {
    const node = mathjs.parse(text) as unknown as MathjsNode;
    return { json: mathjsToJson(node) };
  } catch (error) {
    return { problem: error instanceof Unsupported ? error.message : `계산기 표기로도 읽지 못한다: ${error instanceof Error ? error.message.slice(0, 60) : String(error)}` };
  }
}

function parseLatex(text: string): { json: Json } | { problem: string } {
  ce.pushScope();
  try {
    const raw0 = ce.parse(text, { form: 'raw' });
    if (!raw0.isValid) return { problem: firstProblem(raw0.json as Json) ?? '파서가 식을 읽지 못한다' };
    const json = ce.parse(text).json as Json;
    const bad = firstProblem(json);
    return bad ? { problem: bad } : { json };
  } catch (error) {
    return { problem: `파서 예외: ${error instanceof Error ? error.message : String(error)}` };
  } finally { ce.popScope(); }
}

/** 식 문자열 → 구문 트리. 표기는 역슬래시 명령의 유무로 정한다. 있으면 LaTeX(CE), 없으면 계산기 표기(mathjs)를 먼저 읽는다.
 * 「먼저 성공하는 문법」을 고르면 안 된다. CE 는 exp, sqrt 같은 글자 묶음을 한 글자 기호의 곱으로 읽어 오류 없이 엉뚱한 식을 낸다(실측 2026-10-15) */
type Parsed = { json: Json } | { problem: string; unresolved?: string[] };

function parseTree(raw: string, symbols: Record<string, number>, bound: readonly string[] = []): Parsed {
  const text = unwrapMath(String(raw ?? ''));
  if (!text) return { problem: '식이 비었다' };
  const finish = (json: Json): Parsed => {
    // 상주 페이지는 문항을 이어 읽는다. 읽은 기호가 엔진 전역 범위에 남아 다음 문항의 같은 글자를 다르게 읽는다(실측 2026-10-21 conformance: 접선, 벡터, 삼차 문항 뒤 S1 의 「AB」가 식 목록에서 빠짐). 읽기마다 범위를 열고 닫는다
    ce.pushScope();
    try {
      const boxed = ce.box(json as never);
      const subbed = Object.keys(symbols).length ? boxed.subs(symbols) : boxed;
      const out = subbed.json as Json;
      const bad = firstProblem(out);
      if (bad) return { problem: bad };
      // 값을 대입하고도 남은 기호는 mathcore 에 보내지 않는다. 보내면 슬라이더가 몰래 서고 식은 서지 않는다
      // 슬라이더가 이미 묶고 있는 기호(bound)는 값 없는 기호가 아니다. mathcore 가 이름으로 잇는다
      const open = subbed.unknowns.filter((u) => !NOT_SYMBOLS.includes(u) && !bound.includes(u));
      return open.length ? { problem: `기호 ${open.join(', ')} 의 값이 없다. symbols 에 ${open.join(', ')} 의 값을 준다`, unresolved: open } : { json: out };
    } catch (error) {
      return { problem: `파서 예외: ${error instanceof Error ? error.message : String(error)}` };
    } finally { ce.popScope(); }
  };
  const latexFirst = text.includes('\\');
  const first = latexFirst ? parseLatex(text) : parseAscii(text);
  if ('json' in first) return finish(first.json);
  const second = latexFirst ? parseAscii(text) : parseLatex(text);
  if ('json' in second) return finish(second.json);
  return { problem: latexFirst ? `LaTeX 로: ${first.problem} / 계산기 표기로: ${second.problem}` : `계산기 표기로: ${first.problem} / LaTeX 로: ${second.problem}` };
}

/** 관계식과 정적분은 그대로 세운다. 그 밖의 식(상수, x 만의 식)은 y=… 곡선으로 세운다 */
const standsAlone = (json: Json): boolean => { const h = head(json); return h !== null && (h in RELATIONS || h === 'Integrate'); };

/** 곡선 하나: 식과 문항이 준 조건(예: |x| \le 1)을 받아 mathcore 에 세울 식들을 만든다.
 * 식에 f^{-1}(x) 가 있으면 f 의 곡선으로 바꾸고, 그때 조건의 x 는 y 가 된다. 모델은 변환하지 않고 문항을 베끼기만 한다 */
export function readCurve(expression: string, condition = '', symbols: Record<string, number> = {}, bound: readonly string[] = []): ExprRead {
  const parsed = parseTree(expression, symbols, bound);
  if ('problem' in parsed) return { latex: null, problem: parsed.problem, json: null, unresolved: parsed.unresolved };
  let json = normalizeHead(parsed.json);
  const inverse = hasInverse(json);
  if (inverse) json = swapInverse(json);
  const pieces = readPieces(json);
  if (pieces) return pieces;
  let body: string;
  try {
    body = standsAlone(json) ? print(json).s : `y=${print(json).s}`;
  } catch (error) {
    if (error instanceof Unsupported) return { latex: null, problem: error.message, json };
    throw error;
  }
  if (!condition.trim()) return { latex: body, problem: null, json };
  const cond = parseTree(condition, symbols, bound);
  if ('problem' in cond) return { latex: null, problem: `조건 "${condition}": ${cond.problem}`, json, unresolved: cond.unresolved };
  const v = inverse ? 'y' : 'x';
  const condJson = inverse ? swapInverse(cond.json) : cond.json;
  const intervals = intervalsOf(condJson, v);
  if (!intervals) return { latex: null, problem: `조건 "${condition}" 은 ${v} 하나의 부등식 구간으로 바꿀 수 없다`, json };
  return { latex: null, pieces: intervals.map((it) => ({ latex: body, condition: conditionText(it, v) })), problem: null, json };
}

/** 모델 식 → 구문 트리 → mathcore 식. 읽지 못하면 latex 는 null 이고 problem 에 사유가 든다. 구간별 정의면 pieces 에 조각이 든다.
 * symbols 를 주면 그 문자를 값으로 대입한 트리를 쓴다(슬라이더 개체 없이 수로 그린다) */
export function readExpression(raw: string, symbols: Record<string, number> = {}, bound: readonly string[] = []): ExprRead {
  const parsed = parseTree(raw, symbols, bound);
  if ('problem' in parsed) return { latex: null, problem: parsed.problem, json: null, unresolved: parsed.unresolved };
  let json = normalizeHead(parsed.json);
  const pieces = readPieces(json);
  if (pieces) return pieces;
  try {
    return { latex: standsAlone(json) ? print(json).s : `y=${print(json).s}`, problem: null, json };
  } catch (error) {
    if (error instanceof Unsupported) return { latex: null, problem: error.message, json };
    throw error;
  }
}

/** 값이 없는 기호가 있어도 식의 구조가 읽히는가: 값 없는 기호를 1 로 두고 다시 읽는다. 고를 수 있는 식 목록에 넣을 식을 가리는 데 쓴다 */
function readWithDummies(read: (symbols: Record<string, number>) => ExprRead): ExprRead {
  const dummy: Record<string, number> = {};
  for (let i = 0; i < 3; i += 1) {
    const r = read(dummy);
    if (!r.unresolved?.length) return r;
    for (const u of r.unresolved) dummy[u] = 1;
  }
  return read(dummy);
}

export const readCurveShape = (expression: string, condition = ''): ExprRead => readWithDummies((s) => readCurve(expression, condition, s));
export const readExpressionShape = (raw: string): ExprRead => readWithDummies((s) => readExpression(raw, s));
